// Photo-real Arti hero player
(() => {
  const PIVOT = [0.65, 0.33], FOCUS_WIDE = [0.52, 0.45], FOCUS_TALL = [0.55, 0.5];
  const DELAY = 1500, FADE = 2600, REST = -24;
  const AUTO = [26, -38, REST], HOLDS = [400, 600, 600];   // intro swivel: targets, pauses (ms)
  const reduced = matchMedia('(prefers-reduced-motion: reduce)').matches;
  const net = navigator.connection || {};
  // lite: save-data, slow net, low RAM, calm motion
  const lite = reduced || net.saveData || /(^|-)2g|3g/.test(net.effectiveType || '') || (navigator.deviceMemory || 8) <= 2;
  const clamp = (x, a = 0, b = 1) => Math.min(b, Math.max(a, x));
  const ease = t => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);

  async function init(hero) {
    const url = hero.dataset.frames, prefix = url.replace(/frames\.json.*$/, ''), v = (url.match(/\?v=\d+/) || [''])[0];
    const info = await (await fetch(url)).json();
    const [IW, IH] = info.size, ANG = info.angles, N = ANG.length, A0 = ANG[0], STEP = ANG[1] - ANG[0];
    const small = innerWidth < 990, stride = small ? 2 : 1, Q = small ? 0.8 : 1;   // phones: half the angles, 80% size
    let MIN = ANG[0], MAX = ANG[N - 1];
    const canvas = hero.querySelector('.sc-ph__canvas'), ctx = canvas.getContext('2d');
    const hint = hero.querySelector('.sc-ph__hint'), sws = [...hero.querySelectorAll('.sc-ph__sw')];
    const fname = hero.querySelector('[data-fname]'), shops = [...hero.querySelectorAll('[data-shop]')];
    const FIN = sws.map(b => ({ title: b.dataset.title, id: b.dataset.variant, layer: b.dataset.layer || null }));
    if (lite) hero.classList.add('sc-ph--lite');

    const imgs = {};
    let dirty = true;
    const pad = i => String(i).padStart(2, '0');
    const lit = i => `lit_${pad(i)}`, layerName = (f, i) => `${f}_${pad(i)}`;
    const offOf = f => f && info.offLayers && info.offLayers[f];
    const widthOf = name => {   // CDN width for a frame (0 = original)
      if (Q === 1) return 0;
      const m = name.match(/^(pn|hb|pan|ob)_(\d+|off)$/);
      const r = !m ? [0, 0, IW] : m[2] === 'off' ? offOf(m[1]) : info.layers[m[1]][+m[2]];
      return Math.round(r[2] * Q);
    };
    const load = name => {
      if (!imgs[name]) {
        const im = new Image(), w = widthOf(name);
        im.decoding = 'async';
        im.onload = () => { dirty = true; wake(); };
        im.src = `${prefix}${name}.webp${v}${w ? `${v ? '&' : '?'}width=${w}` : ''}`;
        imgs[name] = im;
      }
      return imgs[name];
    };
    const ready = name => { const im = imgs[name]; return !!im && im.complete && im.naturalWidth > 0; };
    const idxOf = a => (a - A0) / STEP;
    const snap = x => clamp(Math.round(x / stride) * stride, 0, Math.floor((N - 1) / stride) * stride);
    const ZERO = snap(idxOf(0)), RESTI = snap(idxOf(REST));
    const ok = j => j >= 0 && j < N && j % stride === 0 && (!lite || j === RESTI);
    const around = (c, fn) => { for (let d = 0; d < N; d += stride) for (const j of d ? [c - d, c + d] : [c]) if (ok(j)) fn(j); };
    const f0 = FIN.length ? FIN[0].layer : null;   // default finish (first swatch)
    if (lite) {
      load(lit(RESTI));
      if (f0) load(layerName(f0, RESTI));
    } else {
      load('off');
      if (offOf(f0)) load(`${f0}_off`);
      load(lit(ZERO));
      if (f0) load(layerName(f0, ZERO));
      around(ZERO, j => load(lit(j)));
      if (f0) around(ZERO, j => load(layerName(f0, j)));
    }
    const nearest = i => { let hit = -1; around(i, j => { if (hit < 0 && ready(lit(j))) hit = j; }); return hit; };
    const layerAt = (f, i) => ready(layerName(f, i));
    let theta = lite ? REST : 0;
    const here = () => snap(idxOf(theta));

    let fade = document.createElement('canvas'), fctx = fade.getContext('2d');   // lights-on fade only, freed after
    let s = 1, ox = 0, oy = 0, dpr = 1;
    function layout() {
      dpr = Math.min(devicePixelRatio || 1, 2);
      const cw = canvas.width = Math.round(canvas.clientWidth * dpr), ch = canvas.height = Math.round(canvas.clientHeight * dpr);
      if (fade) { fade.width = cw; fade.height = ch; }
      const wide = cw / ch > 1.25, [fx, fy] = wide ? FOCUS_WIDE : FOCUS_TALL;
      s = Math.max(cw / IW, ch / IH);
      ox = (cw - IW * s) * fx;   // same framing as the poster
      oy = (ch - IH * s) * fy;
      MIN = lite ? REST : wide ? -40 : ANG[0];   // desktop: shade stays clear of the copy
      if (lite) MAX = REST;
      dirty = true;
      wake();
    }

    let k = lite ? 1 : 0, vel = 0, aim = REST, mode = lite ? 'free' : 'wait', t0 = 0, autoStep = 0, holdUntil = null;
    let drag = null, touched = false, fin = 0, wipe = null, pending = null;
    const setLevel = val => { k = val; dirty = true; };

    const drawImg = (im, alpha, r, c = ctx) => {
      c.globalAlpha = alpha;
      if (r) c.drawImage(im, ox + r[0] * s, oy + r[1] * s, r[2] * s, r[3] * s);
      else c.drawImage(im, ox, oy, IW * s, IH * s);
    };
    const drawLayer = (f, i) => { if (f && ready(layerName(f, i))) drawImg(imgs[layerName(f, i)], 1, info.layers[f][i]); };
    function draw(now) {
      ctx.globalAlpha = 1;
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      let i = nearest(here());   // nearest rendered angle: no ghosting
      if (i < 0 || k < 1) {   // lights coming on: off frame, lit faded in
        const f = FIN[fin].layer;
        if (ready('off')) drawImg(imgs.off, 1);
        if (offOf(f) && ready(`${f}_off`)) drawImg(imgs[`${f}_off`], 1, offOf(f));
        if (i >= 0 && k > 0 && fade) {
          fctx.globalAlpha = 1;
          fctx.clearRect(0, 0, fade.width, fade.height);
          drawImg(imgs[lit(i)], 1, null, fctx);
          if (f && layerAt(f, i)) drawImg(imgs[layerName(f, i)], 1, info.layers[f][i], fctx);
          ctx.globalAlpha = k;
          ctx.drawImage(fade, 0, 0);
        }
        return;
      }
      const need = [wipe ? wipe.from : FIN[fin].layer, wipe && wipe.to].filter(Boolean);
      if (need.some(f => !layerAt(f, i))) {   // layers loading: hold nearest ready angle
        let hold = -1;
        around(i, j => { if (hold < 0 && ready(lit(j)) && need.every(f => layerAt(f, j))) hold = j; });
        if (hold >= 0) i = hold;
      }
      drawImg(imgs[lit(i)], 1);
      if (!wipe) { drawLayer(FIN[fin].layer, i); return; }
      const R = [wipe.from, wipe.to].filter(Boolean).map(f => info.layers[f][i]);
      const top = Math.min(...R.map(r => r[1])), bot = Math.max(...R.map(r => r[1] + r[3]));
      const t = clamp((now - wipe.t0) / 1200), h = oy + (top + (bot - top) * ease(t)) * s;
      ctx.save(); ctx.beginPath(); ctx.rect(0, h, canvas.width, canvas.height); ctx.clip(); drawLayer(wipe.from, i); ctx.restore();
      ctx.save(); ctx.beginPath(); ctx.rect(0, 0, canvas.width, h); ctx.clip(); drawLayer(wipe.to, i); ctx.restore();
      if (t >= 1) wipe = null;
    }

    // after intro: each finish at rest, for a 1st tap
    const prefetch = () => FIN.forEach(x => { if (x.layer) for (const j of [here() - stride, here(), here() + stride]) if (ok(j)) load(layerName(x.layer, j)); });
    function startWipe() {
      const to = pending;
      pending = null;
      sws.forEach(b => b.classList.remove('is-wait'));
      wipe = reduced ? null : { from: FIN[fin].layer, to: FIN[to].layer, t0: performance.now() };
      fin = to;
      dirty = true;
    }
    // glide: smooth start, fast travel, hard stop
    function move(dt) {
      const d = aim - theta, dist = Math.abs(d);
      if (dist < 0.01 && !vel) return false;
      const [K, VMIN, VMAX, ACC, DEC] = drag ? [30, 120, 900, 6000, 9000] : mode === 'auto' ? [2.2, 36, 55, 60, 120] : [10, 70, 240, 900, 3000];
      const vt = Math.sign(d) * Math.min(VMAX, Math.max(VMIN, K * dist));
      if (mode === 'auto' && Math.abs(vel) < VMIN) vel = Math.sign(d) * Math.min(VMIN, Math.abs(vt));   // glide off the mark
      const a = Math.abs(vt) > Math.abs(vel) && Math.sign(vt) === Math.sign(vel || vt) ? ACC : DEC;
      vel += clamp(vt - vel, -a * dt, a * dt);
      const next = theta + vel * dt;
      if ((aim - next) * d <= 0) { theta = aim; vel = 0; } else theta = next;
      return true;
    }
    function update(now, dt) {
      const firstReady = !f0 || (layerAt(f0, ZERO) && (!offOf(f0) || ready(`${f0}_off`)));
      if (mode === 'wait' && ready('off') && ready(lit(ZERO)) && firstReady) { mode = 'intro'; t0 = now; canvas.classList.add('is-on'); }
      if (mode === 'intro') {
        const t = clamp((now - t0 - DELAY) / FADE);
        setLevel(t * t * (3 - 2 * t));
        if (t >= 1) { mode = 'auto'; aim = theta; autoStep = 0; holdUntil = null; fade = fctx = null; }
      } else if (mode === 'auto') {
        if (theta === aim && !vel) {   // arrived: pause, then the next target
          if (holdUntil === null) holdUntil = now + (HOLDS[autoStep] ?? 0);
          else if (now >= holdUntil) {
            holdUntil = null;
            if (autoStep < AUTO.length) aim = AUTO[autoStep++]; else { mode = 'free'; showHint(); prefetch(); }
          }
        }
        dirty = true;
      }
      if (mode === 'free' && (!lite || ready(lit(RESTI)))) canvas.classList.add('is-on');
      if ((mode === 'free' || mode === 'auto') && move(dt)) dirty = true;
      if (pending !== null && (!FIN[pending].layer || layerAt(FIN[pending].layer, here()))) startWipe();
      if (wipe) dirty = true;
    }

    function showHint() {
      if (touched || !hint || lite) return;
      hint.querySelector('span').textContent = matchMedia('(hover: hover)').matches ? hint.dataset.hover : hint.dataset.touch;
      hint.classList.add('is-on');
      setTimeout(() => hint.classList.remove('is-on'), 7000);
    }
    function interacted() { touched = true; if (hint) hint.classList.remove('is-on'); if (mode !== 'free') { mode = 'free'; setLevel(1); fade = fctx = null; } }
    canvas.addEventListener('pointerdown', e => { if (lite) return; interacted(); drag = { x: e.clientX, th: theta }; canvas.setPointerCapture(e.pointerId); wake(); });
    canvas.addEventListener('pointermove', e => { if (drag) { aim = clamp(drag.th + ((e.clientX - drag.x) / canvas.clientWidth) * 160, MIN, MAX); wake(); } });
    for (const ev of ['pointerup', 'pointercancel']) canvas.addEventListener(ev, () => { drag = null; });
    hero.addEventListener('pointermove', e => {
      if (drag || e.pointerType !== 'mouse' || mode !== 'free' || lite) return;
      const r = canvas.getBoundingClientRect(), px = r.left + (ox + PIVOT[0] * IW * s) / dpr;
      const dx = (e.clientX - px) / (r.width * 0.38);
      aim = dx < 0 ? Math.max(MIN, dx * -MIN) : Math.min(MAX, dx * MAX);
      wake();
    });
    hero.addEventListener('pointerleave', () => { if (!drag && mode === 'free') { aim = REST; wake(); } });

    sws.forEach((b, i) => b.addEventListener('click', () => {
      if (i === fin && pending === null) return;
      const f = FIN[i].layer;
      if (f) around(here(), j => load(layerName(f, j)));   // current angle first
      pending = i;
      if (f && !layerAt(f, here())) b.classList.add('is-wait');
      sws.forEach((x, j) => x.setAttribute('aria-pressed', String(i === j)));
      fname.textContent = FIN[i].title;
      shops.forEach(a => { a.textContent = a.dataset.template.replace('[finish]', FIN[i].title); a.href = `${a.dataset.product}?variant=${FIN[i].id}`; });
      if (mode !== 'free') interacted();
      wake();
    }));

    // loop sleeps when idle or off screen
    let last = 0, on = true, ticking = false;
    function wake() { if (!ticking && on) { ticking = true; last = performance.now(); requestAnimationFrame(tick); } }
    function tick(now) {
      const dt = Math.min(0.05, (now - last) / 1000);
      last = now;
      update(now, dt);
      if (dirty) { draw(now); dirty = false; }
      const busy = mode !== 'free' || drag || vel || theta !== aim || pending !== null || wipe;
      if (on && busy) requestAnimationFrame(tick); else ticking = false;
    }
    new IntersectionObserver(([e]) => { on = e.isIntersecting; if (on) { dirty = true; wake(); } }).observe(hero);
    new ResizeObserver(layout).observe(canvas);
    layout();
  }

  // the hero slides up under the transparent header
  const hd = document.querySelector('.section-header');
  const tuck = () => document.querySelectorAll('.sc-ph').forEach(h => h.style.setProperty('--sc-hd-h', `${hd.offsetHeight}px`));
  if (hd) { tuck(); new ResizeObserver(tuck).observe(hd); }
  // frames load after page load, when idle
  let started = false;
  const start = () => { if (started) return; started = true; document.querySelectorAll('.sc-ph').forEach(h => init(h).catch(() => h.classList.add('sc-ph--static'))); };
  const idle = () => ('requestIdleCallback' in window ? requestIdleCallback(start, { timeout: 1500 }) : setTimeout(start, 200));
  if (document.readyState === 'complete') idle(); else { addEventListener('load', idle, { once: true }); setTimeout(idle, 4000); }   // slow third-party scripts can delay load
})();
